/**
 * Access-reviews dashboard widget (SEC-3 TKT-7).
 *
 * Two counters: cycles due in the next 7 days + overdue cycles. Each
 * links into `/admin/access-reviews` filtered to the relevant tab. The
 * widget is permission-filtered upstream by `access_reviews.manage.org`,
 * so non-reviewers never see it.
 *
 * Cross-References:
 *   - sprint-sec-3-access-recertification.md TKT-7
 *   - GET /access-reviews/cycles
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, ShieldCheck } from 'lucide-react';

import { apiFetch, ApiError } from '@/lib/api';
import type { WidgetProps } from '@/types';

interface CycleRow {
  status: 'open' | 'closed' | 'overdue';
  days_remaining: number;
}

export function AccessReviewsWidget(_props: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const [dueSoon, setDueSoon] = useState<number | null>(null);
  const [overdue, setOverdue] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [open, over] = await Promise.all([
          apiFetch<CycleRow[]>('/access-reviews/cycles?status=open'),
          apiFetch<CycleRow[]>('/access-reviews/cycles?status=overdue'),
        ]);
        if (cancelled) return;
        setDueSoon(open.filter((c) => c.days_remaining <= 7).length);
        setOverdue(over.length);
      } catch (err: unknown) {
        if (cancelled) return;
        // 403 here is the common case for users without the perm; the
        // widget registry shouldn't have surfaced it for them, but we
        // fall silent rather than render a noisy error.
        if (err instanceof ApiError && err.status === 403) {
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Failed to load');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="access-reviews-widget"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 8,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck size={16} />
        <strong style={{ fontSize: '0.95em' }}>Access reviews</strong>
      </header>

      {error && <div style={{ fontSize: 12, color: 'var(--color-danger-dark)' }}>{error}</div>}

      <button
        type="button"
        onClick={() => navigate('/admin/access-reviews?status=overdue')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 6,
          border: '1px solid var(--color-border)',
          background: (overdue ?? 0) > 0 ? 'rgba(220,38,38,0.08)' : 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <AlertTriangle
          size={18}
          style={{
            color: (overdue ?? 0) > 0 ? '#991b1b' : 'var(--color-text-secondary)',
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Overdue
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {overdue === null ? '…' : overdue}
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => navigate('/admin/access-reviews?status=open')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 6,
          border: '1px solid var(--color-border)',
          background: (dueSoon ?? 0) > 0 ? 'rgba(245,158,11,0.08)' : 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Clock
          size={18}
          style={{
            color: (dueSoon ?? 0) > 0 ? '#92400e' : 'var(--color-text-secondary)',
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Due in next 7 days
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {dueSoon === null ? '…' : dueSoon}
          </div>
        </div>
      </button>
    </div>
  );
}
